import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Edit, MapPin, Package } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { typography } from '../../styles/typography';
import { stockOutApi } from './stockOutApi';
import { Card } from '../ui';
import PrimaryButton from '../ui/PrimaryButton';

const StockOutDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchEntry = async () => {
      // First, check if entry data was passed via navigation state (fallback)
      if (location.state?.entry) {
        console.log('StockOutDetails: Using entry from navigation state');
        const entryFromState = location.state.entry;
        setEntry(entryFromState);
        
        setIsLoading(false);
        return;
      }

      if (!id) {
        setError('Stock out entry ID is missing');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError('');

      try {
        const result = await stockOutApi.getStockOutEntry(id);

        if (!result.success) {
        // If API call fails, try using entry from state if available
        if (location.state?.entry) {
          console.log('StockOutDetails: API call failed, using entry from state');
          const entryFromState = location.state.entry;
          setEntry(entryFromState);
          
          setIsLoading(false);
          return;
        }
          throw new Error(result.message || 'Failed to load stock-out entry');
        }

        const entryData = result.entry;
        setEntry(entryData);
        
      } catch (err) {
        console.error('Error fetching stock-out entry:', err);
        
        // If API call fails, try using entry from state if available
        if (location.state?.entry) {
          console.log('StockOutDetails: Error occurred, using entry from state');
          const entryFromState = location.state.entry;
          setEntry(entryFromState);
          
          setIsLoading(false);
          return;
        }
        
        setError(err.message || 'Failed to load stock-out entry. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchEntry();
  }, [id, location.state]);

  const handleEdit = () => {
    if (!entry) {
      console.error('StockOutDetails: Cannot edit - entry is null');
      return;
    }

    // Extract date from entry (try multiple field names) - preserve original date
    const entryDate = entry.stock_date || entry.event_date || entry.date;
    let normalizedEntryDate = null;
    
    if (entryDate) {
      try {
        const dateObj = new Date(entryDate);
        if (!isNaN(dateObj.getTime())) {
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          normalizedEntryDate = `${year}-${month}-${day}`;
        }
      } catch (e) {
        console.error('StockOutDetails: Error parsing date:', e);
      }
    }
    
    console.log('StockOutDetails: Entry date:', normalizedEntryDate);
    console.log('StockOutDetails: Entry date fields:', {
      stock_date: entry.stock_date,
      event_date: entry.event_date,
      date: entry.date,
    });

    // Extract ledger ID from multiple possible sources
    // Backend expects ledger_id, but it might be stored as different field names
    // Priority: URL param id (from route) > entry.ledger_id > other entry fields
    console.log('StockOutDetails: Extracting ledger ID...');
    console.log('StockOutDetails: URL param id:', id);
    console.log('StockOutDetails: Entry object:', entry);
    console.log('StockOutDetails: Entry keys:', Object.keys(entry));
    
    const ledgerId = id || 
                     entry.ledger_id ||
                     entry.ledger_entry_id ||
                     entry.stock_out_record_id || 
                     entry.stock_out_id || 
                     entry.id ||
                     null;
    
    console.log('StockOutDetails: Resolved ledgerId:', ledgerId);

    if (!ledgerId) {
      console.error('StockOutDetails: Cannot edit - ledger ID is missing');
      console.error('StockOutDetails: URL param id:', id);
      console.error('StockOutDetails: Entry keys:', Object.keys(entry));
      console.error('StockOutDetails: Entry object:', JSON.stringify(entry, null, 2));
      alert('Cannot edit: Ledger ID is missing. Please refresh the page and try again.');
      return;
    }

    console.log('StockOutDetails: Preparing to navigate to edit form');
    console.log('StockOutDetails: Ledger ID resolved:', ledgerId);
    console.log('StockOutDetails: Entry object:', JSON.stringify(entry, null, 2));
    console.log('StockOutDetails: Entry keys:', Object.keys(entry));
    console.log('StockOutDetails: Entry stock_item_id:', entry.stock_item_id);
    console.log('StockOutDetails: Entry item_id:', entry.item_id);
    console.log('StockOutDetails: Entry quantity:', entry.quantity);
    console.log('StockOutDetails: Entry reason:', entry.reason);

    // Navigate to create page with entry data for editing
    // Always use the URL param id as the primary source (it's the ledger_id from the route)
    const finalLedgerId = id || ledgerId;
    
    console.log('StockOutDetails: Final ledgerId to pass:', finalLedgerId);
    console.log('StockOutDetails: URL param id:', id);
    console.log('StockOutDetails: Resolved ledgerId:', ledgerId);
    
    if (!finalLedgerId) {
      console.error('StockOutDetails: CRITICAL - No ledger ID available!');
      alert('Cannot edit: Ledger ID is missing. Please refresh the page and try again.');
      return;
    }
    
    // Ensure we pass the ORIGINAL date from the entry (not today's date)
    // This is critical to prevent updating entries for the wrong date
    const dateToPass = normalizedEntryDate || (entry.stock_date || entry.event_date || entry.date);
    
    console.log('StockOutDetails: Navigating to edit with ORIGINAL date:', dateToPass);
    console.log('StockOutDetails: Normalized entry date:', normalizedEntryDate);
    console.log('StockOutDetails: Entry date fields:', {
      stock_date: entry.stock_date,
      event_date: entry.event_date,
      date: entry.date,
    });
    
    navigate('/onboarding-stock-out/create', {
      state: {
        editMode: true,
        entryId: finalLedgerId, // Use URL param id first, then resolved ledgerId (backend expects ledger_id)
        entry: entry, // Make sure entry is passed
        date: dateToPass, // Use ORIGINAL date from entry, not today's date
        siteId: entry.site_id || entry.siteId,
        siteName: entry.site_name || entry.siteName,
        referenceId: entry.reference_id || entry.referenceId,
        // Also pass as items array for backward compatibility
        items: [{
          stock_item_id: entry.stock_item_id || entry.item_id,
          item_id: entry.stock_item_id || entry.item_id,
          itemName: entry.item_name || entry.itemName || entry.stock_item_name,
          quantity: entry.quantity || entry.qty,
          quantityOrWeight: entry.quantity || entry.qty,
          reason: entry.reason,
          action: entry.reason === 'Sold' ? 'Sell' : 'Return',
          selling_price: entry.selling_price || entry.sellingPrice,
          pricePerKg: entry.selling_price || entry.sellingPrice,
          notes: entry.notes || entry.reason,
        }],
      }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  if (isLoading) {
    return (
      <div className="w-full min-h-screen">
        <div className="mx-auto h-auto">
          <Card.Light className="p-8 text-center">
            <p className="text-gray-500 text-lg">Loading stock-out entry...</p>
          </Card.Light>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full min-h-screen">
        <div className="mx-auto h-auto">
          <Card.Light className="p-8">
            <div className="text-center">
              <p className="text-red-600 text-lg mb-4">{error}</p>
              <PrimaryButton onClick={() => navigate('/onboarding-stock-out')}>
                Back to Stock Out
              </PrimaryButton>
            </div>
          </Card.Light>
        </div>
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="w-full min-h-screen">
        <div className="mx-auto h-auto">
          <Card.Light className="p-8 text-center">
            <p className="text-gray-500 text-lg">Stock-out entry not found</p>
            <PrimaryButton 
              onClick={() => navigate('/onboarding-stock-out')}
              className="mt-4"
            >
              Back to Stock Out
            </PrimaryButton>
          </Card.Light>
        </div>
      </div>
    );
  }

  // Extract entry details - handle different API response structures
  const entryDate = entry.stock_date || entry.event_date || entry.date || entry.created_at;
  const siteName = entry.site_name || entry.siteName || 'Unknown Site';
  const itemName = entry.item_name || entry.itemName || entry.stock_item_name || 'Unknown Item';
  const quantity = entry.quantity || entry.qty || 0;
  const unit = entry.unit_of_measure || entry.unit || '';
  const reason = entry.reason || 'N/A';
  const sellingPrice = entry.selling_price || entry.sellingPrice || null;
  const notes = entry.notes || entry.reason || '';
  const referenceId = entry.reference_id || entry.referenceId || 'N/A';
  const totalValue = sellingPrice ? (parseFloat(quantity) * parseFloat(sellingPrice)) : null;

  return (
    <div className="w-full min-h-screen">
      <div className="mx-auto h-auto max-w-4xl">
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          className="space-y-6"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate('/onboarding-stock-out')}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Back to Stock Out</span>
            </button>
            <PrimaryButton onClick={handleEdit} className="inline-flex items-center gap-2">
              <Edit className="w-4 h-4" />
              Edit Entry
            </PrimaryButton>
          </div>

          {/* Title */}
          <div className="mb-6">
            <h1 className={typography.h2} style={{ color: '#000000' }}>
              Stock Out Details
            </h1>
            <p className="mt-2 text-gray-600">
              View detailed information about this stock-out entry
            </p>
          </div>

          {/* Main Details Card */}
          <Card.Light className="p-6">
            <div className="space-y-6">
              {/* Date and Site */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Date</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatDate(entryDate)}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Site</p>
                    <p className="text-lg font-semibold text-gray-900">{siteName}</p>
                  </div>
                </div>
              </div>

              {/* Reference ID */}
              {referenceId !== 'N/A' && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm font-medium text-gray-500 mb-1">Reference ID</p>
                  <p className="text-base text-gray-900 font-mono">{referenceId}</p>
                </div>
              )}

              {/* Item Details */}
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <Package className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-500 mb-1">Stock Item</p>
                    <p className="text-lg font-semibold text-gray-900">{itemName}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-1">Quantity</p>
                    <p className="text-base text-gray-900 font-semibold">
                      {quantity} {unit}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-1">Reason</p>
                    <p className="text-base text-gray-900 font-semibold">
                      {reason === 'Sold' ? (
                        <span className="text-green-600">Sold</span>
                      ) : reason === 'Returned' ? (
                        <span className="text-orange-600">Returned</span>
                      ) : (
                        reason
                      )}
                    </p>
                  </div>

                  {sellingPrice !== null && (
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">Selling Price</p>
                      <p className="text-base text-gray-900 font-semibold">
                        {formatCurrency(sellingPrice)}
                      </p>
                    </div>
                  )}
                </div>

                {totalValue !== null && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <p className="text-sm font-medium text-gray-500">Total Value</p>
                      <p className="text-xl font-bold text-green-600">
                        {formatCurrency(totalValue)}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Notes */}
              {notes && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm font-medium text-gray-500 mb-2">Notes</p>
                  <p className="text-base text-gray-700 bg-gray-50 rounded-lg p-3">
                    {notes}
                  </p>
                </div>
              )}

              {/* Additional Metadata */}
              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm font-medium text-gray-500 mb-3">Additional Information</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {entry.created_at && (
                    <div>
                      <p className="text-gray-500">Created At</p>
                      <p className="text-gray-900 font-medium">
                        {formatDate(entry.created_at)}
                      </p>
                    </div>
                  )}
                  {entry.created_by && (
                    <div>
                      <p className="text-gray-500">Created By</p>
                      <p className="text-gray-900 font-medium">
                        {entry.created_by}
                      </p>
                    </div>
                  )}
                  {entry.stock_out_record_id && (
                    <div>
                      <p className="text-gray-500">Record ID</p>
                      <p className="text-gray-900 font-mono text-xs">
                        {entry.stock_out_record_id}
                      </p>
                    </div>
                  )}
                  {entry.stock_item_id && (
                    <div>
                      <p className="text-gray-500">Stock Item ID</p>
                      <p className="text-gray-900 font-mono text-xs">
                        {entry.stock_item_id}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card.Light>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4">
            <button
              onClick={() => navigate('/onboarding-stock-out')}
              className="px-6 py-2 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              Back to List
            </button>
            <PrimaryButton onClick={handleEdit} className="inline-flex items-center gap-2">
              <Edit className="w-4 h-4" />
              Edit Entry
            </PrimaryButton>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default StockOutDetails;

